<onsip-header-main>
  <onsip-header-page-title>Phones</onsip-header-page-title>
</onsip-header-main>
<onsip-loading-container
  [deferCond]="loadingStateComplete$"
  [loadingMessage]="'ONSIP_I18N.SAVING_CHANGES'"
>
  <form
    [formGroup]="formGroup"
    #form="ngForm"
    onsipFormTracking
    name="admin-phone-setup-wizard"
    id="admin-phone-setup-wizard"
    (ngSubmit)="submit()"
  >
    <mat-horizontal-stepper
      #stepper
      linear
      class="onsip-stepper-disabled-header onsip-header-full-width"
    >
      <mat-step completed="false">
        <ng-template matStepLabel>
          @if ((mobileBreakpoint | async) === false) {
          <span>Basic setup</span>
          }
        </ng-template>
        <div onsip-grid-placement-shelf>
          <div class="basic-setup-column">
            <div class="mat-headline-5">Basic setup</div>
            <section class="with-mat-form-field">
              <mat-form-field appearance="outline">
                <mat-label>Make</mat-label>
                <mat-select formControlName="make" required>
                  @for (make of phoneMakes; track make) {
                  <mat-option [value]="make">
                    {{ make }}
                  </mat-option>
                  }
                </mat-select>
                <mat-error>Phone make is required.</mat-error>
              </mat-form-field>
            </section>
            <section>
              <mat-form-field appearance="outline">
                <mat-label>Model</mat-label>
                <mat-select formControlName="model" required>
                  @for (model of phoneModel; track model) {
                  <mat-option [value]="model.key">
                    {{ model.name }}
                  </mat-option>
                  }
                </mat-select>
                <mat-error>Phone model is required.</mat-error>
                <mat-hint>
                  If your make and model is not listed,
                  <a class="support-link" href="https://support.onsip.com" target="_blank">
                    visit the knowledgebase
                  </a>
                </mat-hint>
              </mat-form-field>
            </section>
            <section>
              <mat-form-field appearance="outline">
                <mat-label>MAC address</mat-label>
                <input
                  matInput
                  formControlName="macAddress"
                  required
                  [maxlength]="macAddressMaxLength"
                  pattern="^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})|([0-9a-fA-F]{4}\.[0-9a-fA-F]{4}\.[0-9a-fA-F]{4})|([0-9a-fA-F]{12})$"
                />
                @if (formGroup.controls.macAddress.hasError('required')) {
                <mat-error>Phone MAC address is required.</mat-error>
                } @if (formGroup.controls.macAddress.hasError('pattern')) {
                <mat-error>Invalid MAC address</mat-error>
                }
                <mat-hint>The MAC address is labeled on the back of the phone</mat-hint>
              </mat-form-field>
            </section>
            <section class="with-mat-form-field">
              <mat-form-field appearance="outline">
                <mat-label>Time zone (GMT)</mat-label>
                <mat-select formControlName="timezone" required>
                  @for (timezone of timezoneOptions; track timezone) {
                  <mat-option [value]="timezone.value">
                    {{ timezone.text }}
                  </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </section>
            <section>
              <span class="mat-body-1 span-with-icon phone-password">
                Phone password
                <mat-icon
                  onsip-question-mark-tooltip
                  matTooltipClass="onsip-tooltip-background"
                  matTooltip="Changing a phone’s default password is an added security measure."
                  matTooltipPosition="above"
                >
                  help_outline
                </mat-icon>
              </span>
              <mat-radio-group
                color="primary"
                formControlName="orgPassword"
                class="basic-setup-phone-password-radio-group"
              >
                <!-- By default, admin portal will generate a new password, but we let the user specify if they want to use the org's password instead -->
                <mat-radio-button [value]="false">Create a password for me</mat-radio-button>
                <mat-radio-button [value]="true">Use the organization's password</mat-radio-button>
              </mat-radio-group>
            </section>
          </div>
          <section class="advanced-setup-column">
            <div class="mat-headline-5">Advanced (optional)</div>
            <section>
              @if (phoneFeatures.Directory) {
              <span class="span-with-icon">
                <mat-checkbox color="primary" class="mat-body-1" formControlName="directory">
                  <mat-label>Company Directory</mat-label>
                </mat-checkbox>
                <mat-icon
                  class="question-hover-icon"
                  onsip-question-mark-tooltip
                  matTooltipClass="onsip-tooltip-background"
                  matTooltip="Polycom only. A Company directory adds all users on a selected PBX to the current phone. This option disables local changes to the directory."
                  matTooltipPosition="above"
                >
                  help_outline
                </mat-icon>
              </span>
              }
            </section>
            <section>
              @if (phoneFeatures.Intercom) {
              <span class="span-with-icon">
                <mat-checkbox color="primary" class="mat-body-1" formControlName="intercom">
                  <mat-label>Intercom</mat-label>
                </mat-checkbox>
                <mat-icon
                  class="question-hover-icon"
                  onsip-question-mark-tooltip
                  matTooltipClass="onsip-tooltip-background"
                  matTooltip="Polycom VVX models only. The intercom feature allows you to talk to another user without any action required on their end."
                  matTooltipPosition="above"
                >
                  help_outline
                </mat-icon>
              </span>
              }
            </section>
            <section>
              @if (phoneFeatures.NATKeepalive) {
              <span class="span-with-icon">
                <mat-checkbox color="primary" class="mat-body-1" formControlName="natKeepalive">
                  <mat-label>NAT Keepalive</mat-label>
                </mat-checkbox>
                <mat-icon
                  class="question-hover-icon"
                  onsip-question-mark-tooltip
                  matTooltipClass="onsip-tooltip-background"
                  matTooltip="NAT keepalive sends a message from one device to another to check that the link between the two is operating. OnSIP provides this service by default, so it’s not necessary to enable an additional phone based service."
                  matTooltipPosition="above"
                >
                  help_outline
                </mat-icon>
              </span>
              }
            </section>
            <section>
              @if (phoneFeatures.EasyTransfer) {
              <span class="span-with-icon">
                <mat-checkbox color="primary" class="mat-body-1" formControlName="easyTransfer">
                  <mat-label>Easy transfer</mat-label>
                </mat-checkbox>
                <mat-icon
                  class="question-hover-icon"
                  onsip-question-mark-tooltip
                  matTooltipClass="onsip-tooltip-background"
                  matTooltip="Polycom and select Cisco models only. Easy transfer allows users to transfer calls directly to another line without speaking with the other party first (also known as blind transfer)."
                  matTooltipPosition="above"
                >
                  help_outline
                </mat-icon>
              </span>
              }
            </section>
            <section>
              @if (phoneFeatures.WebInterface) {
              <span class="span-with-icon">
                <mat-checkbox color="primary" class="mat-body-1" formControlName="webInterface">
                  <mat-label>Enable web interface</mat-label>
                </mat-checkbox>
                <mat-icon
                  class="question-hover-icon"
                  onsip-question-mark-tooltip
                  matTooltipClass="onsip-tooltip-background"
                  matTooltip="Enables a phone to be configured via a web interface hosted by the phone’s manufacturer. This option poses a security risk and is not recommended."
                  matTooltipPosition="above"
                >
                  help_outline
                </mat-icon>
              </span>
              }
            </section>
            <section>
              @if (phoneFeatures.EndToEndEncryption) {
              <span class="span-with-icon">
                <mat-checkbox
                  color="primary"
                  class="mat-body-1"
                  formControlName="endToEndEncryption"
                >
                  <mat-label>End to End Encryption</mat-label>
                </mat-checkbox>
                <mat-icon
                  class="question-hover-icon"
                  onsip-question-mark-tooltip
                  matTooltipClass="onsip-tooltip-background"
                  matTooltip="Enables SDES-SRTP encryption of all media to and from the device."
                  matTooltipPosition="above"
                >
                  help_outline
                </mat-icon>
              </span>
              }
            </section>
            <section>
              @if (phoneFeatures.DigitMap) {
              <mat-form-field appearance="outline">
                <mat-label>Custom digit map</mat-label>
                <!-- digitmap is limited to 768 byte -->
                <input matInput maxlength="768" formControlName="digitMap" />
                <mat-hint
                  >Changes the default length of digits required for an extension to dial
                  automatically</mat-hint
                >
              </mat-form-field>
              } @if (noFeatures) {
              <span class="no-features">
                Advanced features are unavailable for this make and model. To see which makes and
                models feature advanced options,
                <a class="support-link" href="https://support.onsip.com" target="_blank"
                  >visit the knowledgebase.</a
                >
              </span>
              }
            </section>
          </section>
        </div>
        <div class="navigation-buttons no-back-button">
          <span>
            <button
              mat-button
              type="button"
              color="primary"
              class="cancel-button"
              [routerLink]="views.PHONES"
            >
              {{ "ONSIP_I18N.CANCEL" | translate }}
            </button>
            <button mat-flat-button type="button" color="primary" (click)="nextStep()">Next</button>
          </span>
        </div>
      </mat-step>
      <mat-step completed="false">
        <ng-template matStepLabel>
          @if ((mobileBreakpoint | async) === false) {
          <span>Assign user(s)</span>
          }
        </ng-template>
        <div class="mat-headline-4 assign-users-title">Assign user(s) to a line</div>
        <div onsip-grid-placement-shelf class="assign-step-grid">
          <div class="busy-lamp-column">
            <div class="blf-and-expansion-module">
              <div class="blf">
                <div class="mat-body-1 span-with-icon">
                  Busy Lamp Field
                  <mat-icon
                    onsip-question-mark-tooltip
                    matTooltipClass="onsip-tooltip-background"
                    matTooltip="Busy Lamp Field is a light on a phone’s display that indicates which lines are in use. This service applies to all phones on your PBX that support the feature."
                    matTooltipPosition="above"
                  >
                    help_outline
                  </mat-icon>
                </div>
                <div class="mat-body-2">{{ getPricingText() }}</div>
                <span class="blf-slide-toggle">
                  <mat-slide-toggle
                    color="primary"
                    formControlName="blf"
                    (change)="toggleBLF($event)"
                  ></mat-slide-toggle>
                </span>
                <div class="blf-hint">
                  Busy Lamp Field is {{ formGroup.controls.blf.value ? "enabled" : "disabled" }} on
                  your PBX
                </div>
              </div>
              <div class="expansion-module">
                <span class="mat-body-1 span-with-icon m-b-12">
                  Expansion module
                  <mat-icon
                    class="question-hover-icon"
                    onsip-question-mark-tooltip
                    matTooltipClass="onsip-tooltip-background"
                    matTooltip="An expansion module is a physical attachment that adds additional phone lines."
                    matTooltipPosition="above"
                  >
                    help_outline
                  </mat-icon>
                </span>
                @if (expansionModuleName) {
                <div>
                  <mat-radio-group
                    class="expansion-radio-group"
                    color="primary"
                    formControlName="expansionModule"
                  >
                    <mat-radio-button [value]="false">None</mat-radio-button>
                    <mat-radio-button [value]="true">{{
                      expansionModuleName || "None"
                    }}</mat-radio-button>
                  </mat-radio-group>
                </div>
                } @else {
                <div class="no-expansion">
                  Expansion module is unavailable for your phone’s make and model. To see which
                  makes and models feature expansion modules,
                  <a class="support-link" href="https://support.onsip.com" target="_blank"
                    >visit the knowledgebase.</a
                  >
                </div>
                }
              </div>
            </div>
          </div>
          <div class="assign-users-column" formGroupName="linesInfo">
            @for (line of lineOptions; track line; let i = $index) {
            <div class="mat-body-1 m-b-12">{{ getLineName(i) }}</div>
            <div onsip-grid-placement-shelf class="lines-edit-grid">
              <div class="name-line-column">
                <onsip-address-autocomplete
                  [addressTypes]="['user']"
                  noneSelectedMessage="Not assigned"
                  [preventBrowse]="i !== 0"
                  [parentForm]="form"
                  formControlName="nameLine{{ i + 1 }}"
                  [errorMessage]="getLineError(i + 1)"
                >
                </onsip-address-autocomplete>
              </div>
              <div class="phone-line-column">
                <mat-radio-group
                  formControlName="typeLine{{ i + 1 }}"
                  (change)="handleLineChange($event, i)"
                  class="lines-edit-radio-group"
                  color="primary"
                >
                  @if (line.type !== 'blf') {
                  <mat-radio-button value="Phone line">Phone line</mat-radio-button>
                  } @if (line.type !== 'phone') {
                  <mat-radio-button value="Busy lamp">Busy Lamp</mat-radio-button>
                  }
                </mat-radio-group>
              </div>
              <div class="call-waiting-column">
                @if (!line.isExpansion && line.type !== 'blf') {
                <mat-checkbox color="primary" formControlName="callWaitingLine{{ i + 1 }}"
                  >Call waiting</mat-checkbox
                >
                }
              </div>
            </div>
            @if (i !== lineOptions.length - 1) {
            <mat-divider></mat-divider>
            } }
          </div>
        </div>
        @if ((mobileBreakpoint | async) === false) {
        <div class="navigation-buttons">
          <button mat-stroked-button type="button" color="primary" (click)="backStep()">
            Back
          </button>
          <span>
            <button
              mat-button
              type="button"
              color="primary"
              class="cancel-button"
              [routerLink]="views.PHONES"
            >
              {{ "ONSIP_I18N.CANCEL" | translate }}
            </button>
            <button mat-flat-button type="submit" color="primary">Next</button>
          </span>
        </div>
        } @if (mobileBreakpoint | async) {
        <div class="navigation-mobile-buttons">
          <button mat-flat-button type="submit" color="primary">Next</button>
          <button mat-stroked-button type="button" color="primary" (click)="backStep()">
            Back
          </button>
          <button mat-button type="button" color="primary" [routerLink]="views.PHONES">
            {{ "ONSIP_I18N.CANCEL" | translate }}
          </button>
        </div>
        }
      </mat-step>
      <mat-step>
        <ng-template matStepLabel>
          @if ((mobileBreakpoint | async) === false) {
          <span>Finish setup</span>
          }
        </ng-template>
        <div class="finish-setup-container">
          <div class="mat-headline-4">Finish phone setup</div>
          <div class="mat-body-2">
            In order to successfully complete the phone setup process, ensure that your phone is:
          </div>
          <div class="mat-body-1">Plugged in to a power source</div>
          <div class="mat-body-1">Connected to your internet</div>
          <div class="mat-body-1">Connected to 'boot.onsip.com'</div>
          <div class="mat-body-2">
            Once you’ve ensured the above criteria is met, reboot/restart your phone to finish
            setup. For additional help, visit
            <a class="support-link" href="https://support.onsip.com" target="_blank">
              OnSIP's knowledgebase
            </a>
            and/or contact our
            <a class="support-link" [routerLink]="views.CONTACT_SUPPORT">
              {{ "ONSIP_I18N.SUPPORT_TEAM" | translate }}
            </a>
          </div>
        </div>
        <div class="navigation-buttons no-back-button">
          <button mat-flat-button type="button" color="primary" [routerLink]="views.PHONES">
            Finish setup
          </button>
        </div>
      </mat-step>
    </mat-horizontal-stepper>
  </form>
</onsip-loading-container>
<span class="mat-body-2 contact-support"
  >{{ "ONSIP_I18N.NEED_HELP_CONTACT_OUR" | translate }}
  <a class="support-link" [routerLink]="views.CONTACT_SUPPORT">
    {{ "ONSIP_I18N.SUPPORT_TEAM" | translate }}
  </a>
</span>
